export interface PrayerStep {
  instruction: string;
  arabic?: string;
  transliteration?: string;
  translation?: string;
  image?: string; // Placeholder for image asset name
  audioStart?: number; // Start time in seconds
  audioEnd?: number; // End time in seconds
  isDynamic?: boolean; // New flag for dynamic content (like Fatiha)
  surahId?: number; // ID of the Surah for dynamic display
  duration?: number; // Duration in seconds for movement steps
  customAudioUrl?: string; // Optional override for specific step audio
}

export interface PrayerGuide {
  id: string;
  name: string;
  rakyat: number;
  type: 'Fard' | 'Sunnah';
  time: string;
  steps: PrayerStep[];
  audioUrl?: string;
}

export const prayerGuides: Record<string, PrayerGuide> = {
  'Fajr': {
    id: 'fajr',
    name: 'Fajr',
    rakyat: 2,
    type: 'Fard',
    time: 'Dawn to Sunrise',
    steps: [
      // --- Raka'at 1 (Loud) ---
      {
        instruction: 'Make Niyyah (Intention) to pray 2 Raka\'at Fard of Fajr.',
        arabic: 'نَوَيْتُ صَلَاةَ الْفَجْرِ',
        transliteration: 'Nawaytu salata al-Fajr',
        translation: 'I intend to perform the Fajr prayer.',
        duration: 5
      },
      {
        instruction: 'Takbir al-Ihram (Start)',
        arabic: 'اللهُ أَكْبَرُ',
        transliteration: 'Allahu Akbar',
        translation: 'Allah is the Greatest',
        image: 'takbir',
        duration: 3
      },
      {
        instruction: 'Recite Surah Al-Fatiha',
        arabic: 'بِسْمِ ٱللَّهِ ٱلرَّحْمَـٰنِ ٱلرَّحِيمِ...',
        transliteration: 'Bismillahi r-rahmani r-rahim...',
        translation: '(The Opening Surah) - Recited Loudly',
        audioStart: 6.5,
        audioEnd: 65,
        isDynamic: true,
        surahId: 1
      },
      {
        instruction: 'Recite another Surah',
        arabic: 'قُلْ هُوَ ٱللَّهُ أَحَدٌ...',
        transliteration: 'Qul huwa llāhu aḥad...',
        translation: '(The Sincerity) - Recited Loudly',
        customAudioUrl: 'https://server8.mp3quran.net/afs/112.mp3',
        audioStart: 5.5,
        duration: 10,
        isDynamic: true,
        surahId: 112
      },
      {
        instruction: 'Ruku (Bowing)',
        arabic: 'سُبْحَانَ رَبِّيَ الْعَظِيمِ',
        transliteration: 'Subhana Rabbiyal Azeem',
        translation: 'Glory be to my Lord, the Magnificent (3 times)',
        image: 'ruku',
        duration: 8
      },
      {
        instruction: 'Samiallah (Rising)',
        arabic: 'سَمِعَ اللَّهُ لِمَنْ حَمِدَهُ',
        transliteration: 'Sami Allahu liman hamidah',
        translation: 'Allah hears those who praise Him',
        image: 'standing',
        duration: 4
      },
      {
        instruction: 'Rabbana (Standing)',
        arabic: 'رَبَّنَا وَلَكَ الْحَمْدُ',
        transliteration: 'Rabbana wa lakal hamd',
        translation: 'Our Lord, and to You is all praise',
        duration: 4
      },
      {
        instruction: 'Sujood (Prostration 1)',
        arabic: 'سُبْحَانَ رَبِّيَ الأَعْلَى',
        transliteration: 'Subhana Rabbiyal A\'la',
        translation: 'Glory be to my Lord, the Most High (3 times)',
        image: 'sujood',
        duration: 8
      },
      {
        instruction: 'Sitting between Sujood',
        arabic: 'رَبِّ اغْفِرْ لِي',
        transliteration: 'Rabbighfir li',
        translation: 'O my Lord, forgive me',
        image: 'sitting',
        duration: 5
      },
      {
        instruction: 'Sujood (Prostration 2)',
        arabic: 'سُبْحَانَ رَبِّيَ الأَعْلَى',
        transliteration: 'Subhana Rabbiyal A\'la',
        translation: 'Glory be to my Lord, the Most High (3 times)',
        image: 'sujood',
        duration: 8
      },

      // --- Raka'at 2 (Loud) ---
      {
        instruction: 'Stand up for 2nd Raka\'at',
        translation: 'Say "Allahu Akbar" and stand up.',
        duration: 4
      },
      {
        instruction: 'Recite Surah Al-Fatiha',
        arabic: 'بِسْمِ ٱللَّهِ ٱلرَّحْمَـٰنِ ٱلرَّحِيمِ...',
        transliteration: 'Bismillahi r-rahmani r-rahim...',
        translation: '(The Opening Surah) - Recited Loudly',
        audioStart: 6.5,
        audioEnd: 65,
        isDynamic: true,
        surahId: 1
      },
      {
        instruction: 'Recite another Surah',
        arabic: 'قُلْ أَعُوذُ بِرَبِّ ٱلْفَلَقِ...',
        transliteration: 'Qul aʿūdhu bi-rabbi l-falaq...',
        translation: '(The Daybreak) - Recited Loudly',
        customAudioUrl: 'https://server8.mp3quran.net/afs/113.mp3',
        audioStart: 6,
        duration: 15,
        isDynamic: true,
        surahId: 113
      },
      {
        instruction: 'Ruku (Bowing)',
        arabic: 'سُبْحَانَ رَبِّيَ الْعَظِيمِ',
        transliteration: 'Subhana Rabbiyal Azeem',
        translation: 'Glory be to my Lord, the Magnificent (3 times)',
        image: 'ruku',
        duration: 8
      },
      {
        instruction: 'Samiallah (Rising)',
        arabic: 'سَمِعَ اللَّهُ لِمَنْ حَمِدَهُ',
        transliteration: 'Sami Allahu liman hamidah',
        translation: 'Allah hears those who praise Him',
        image: 'standing',
        duration: 4
      },
      {
        instruction: 'Sujood (Prostration 1)',
        arabic: 'سُبْحَانَ رَبِّيَ الأَعْلَى',
        transliteration: 'Subhana Rabbiyal A\'la',
        translation: 'Glory be to my Lord, the Most High (3 times)',
        image: 'sujood',
        duration: 8
      },
      {
        instruction: 'Sitting between Sujood',
        arabic: 'رَبِّ اغْفِرْ لِي',
        transliteration: 'Rabbighfir li',
        translation: 'O my Lord, forgive me',
        image: 'sitting',
        duration: 5
      },
      {
        instruction: 'Sujood (Prostration 2)',
        arabic: 'سُبْحَانَ رَبِّيَ الأَعْلَى',
        transliteration: 'Subhana Rabbiyal A\'la',
        translation: 'Glory be to my Lord, the Most High (3 times)',
        image: 'sujood',
        duration: 8
      },
      {
        instruction: 'Final Tashahhud & Salawat',
        arabic: 'التَّحِيَّاتُ لِلَّهِ...',
        transliteration: 'At-tahiyyatu lillahi...',
        translation: '(Recite full Tashahhud and Durood Ibrahim)',
        image: 'sitting',
        duration: 25
      },
      {
        instruction: 'Tasleem (End)',
        arabic: 'السَّلَامُ عَلَيْكُمْ وَرَحْمَةُ اللهِ',
        transliteration: 'Assalamu alaykum wa rahmatullah',
        translation: 'Peace be upon you and the mercy of Allah (Right then Left)',
        duration: 6
      }
    ],
    audioUrl: 'https://server8.mp3quran.net/afs/001.mp3'
  },
  'Dhuhr': {
    id: 'dhuhr',
    name: 'Dhuhr',
    rakyat: 4,
    type: 'Fard',
    time: 'Noon to Mid-Afternoon',
    steps: [
      // --- Raka'at 1 ---
      {
        instruction: 'Make Niyyah (Intention) to pray 4 Raka\'at Fard of Dhuhr.',
        arabic: 'نَوَيْتُ صَلَاةَ الظُّهْرِ',
        transliteration: 'Nawaytu salata al-Dhuhr',
        translation: 'I intend to perform the Dhuhr prayer.',
        duration: 5
      },
      {
        instruction: 'Takbir al-Ihram (Start)',
        arabic: 'اللهُ أَكْبَرُ',
        transliteration: 'Allahu Akbar',
        translation: 'Allah is the Greatest',
        image: 'takbir',
        duration: 3
      },
      {
        instruction: 'Recite Surah Al-Fatiha',
        arabic: 'بِسْمِ ٱللَّهِ ٱلرَّحْمَـٰنِ ٱلرَّحِيمِ...',
        transliteration: 'Bismillahi r-rahmani r-rahim...',
        translation: '(The Opening Surah) - Recited Silently',
        audioStart: 6.5,
        audioEnd: 65,
        isDynamic: true,
        surahId: 1
      },
      {
        instruction: 'Recite another Surah',
        arabic: 'قُلْ هُوَ ٱللَّهُ أَحَدٌ...',
        transliteration: 'Qul huwa llāhu aḥad...',
        translation: '(The Sincerity) - Recited Silently',
        customAudioUrl: 'https://server8.mp3quran.net/afs/112.mp3',
        audioStart: 5.5,
        duration: 10,
        isDynamic: true,
        surahId: 112
      },
      {
        instruction: 'Ruku (Bowing)',
        arabic: 'سُبْحَانَ رَبِّيَ الْعَظِيمِ',
        transliteration: 'Subhana Rabbiyal Azeem',
        translation: 'Glory be to my Lord, the Magnificent (3 times)',
        image: 'ruku',
        duration: 8
      },
      {
        instruction: 'Samiallah (Rising)',
        arabic: 'سَمِعَ اللَّهُ لِمَنْ حَمِدَهُ',
        transliteration: 'Sami Allahu liman hamidah',
        translation: 'Allah hears those who praise Him',
        image: 'standing',
        duration: 4
      },
      {
        instruction: 'Rabbana (Standing)',
        arabic: 'رَبَّنَا وَلَكَ الْحَمْدُ',
        transliteration: 'Rabbana wa lakal hamd',
        translation: 'Our Lord, and to You is all praise',
        duration: 4
      },
      {
        instruction: 'Sujood (Prostration 1)',
        arabic: 'سُبْحَانَ رَبِّيَ الأَعْلَى',
        transliteration: 'Subhana Rabbiyal A\'la',
        translation: 'Glory be to my Lord, the Most High (3 times)',
        image: 'sujood',
        duration: 8
      },
      {
        instruction: 'Sitting between Sujood',
        arabic: 'رَبِّ اغْفِرْ لِي',
        transliteration: 'Rabbighfir li',
        translation: 'O my Lord, forgive me',
        image: 'sitting',
        duration: 5
      },
      {
        instruction: 'Sujood (Prostration 2)',
        arabic: 'سُبْحَانَ رَبِّيَ الأَعْلَى',
        transliteration: 'Subhana Rabbiyal A\'la',
        translation: 'Glory be to my Lord, the Most High (3 times)',
        image: 'sujood',
        duration: 8
      },

      // --- Raka'at 2 ---
      {
        instruction: 'Stand up for 2nd Raka\'at',
        translation: 'Say "Allahu Akbar" and stand up.',
        duration: 4
      },
      {
        instruction: 'Recite Surah Al-Fatiha',
        arabic: 'بِسْمِ ٱللَّهِ ٱلرَّحْمَـٰنِ ٱلرَّحِيمِ...',
        transliteration: 'Bismillahi r-rahmani r-rahim...',
        translation: '(The Opening Surah) - Recited Silently',
        audioStart: 6.5,
        audioEnd: 65,
        isDynamic: true,
        surahId: 1
      },
      {
        instruction: 'Recite another Surah',
        arabic: 'قُلْ أَعُوذُ بِرَبِّ ٱلْفَلَقِ...',
        transliteration: 'Qul aʿūdhu bi-rabbi l-falaq...',
        translation: '(The Daybreak) - Recited Silently',
        customAudioUrl: 'https://server8.mp3quran.net/afs/113.mp3',
        audioStart: 6,
        duration: 15,
        isDynamic: true,
        surahId: 113
      },
      {
        instruction: 'Ruku (Bowing)',
        arabic: 'سُبْحَانَ رَبِّيَ الْعَظِيمِ',
        transliteration: 'Subhana Rabbiyal Azeem',
        translation: 'Glory be to my Lord, the Magnificent (3 times)',
        image: 'ruku',
        duration: 8
      },
      {
        instruction: 'Samiallah (Rising)',
        arabic: 'سَمِعَ اللَّهُ لِمَنْ حَمِدَهُ',
        transliteration: 'Sami Allahu liman hamidah',
        translation: 'Allah hears those who praise Him',
        image: 'standing',
        duration: 4
      },
      {
        instruction: 'Sujood (Prostration 1)',
        arabic: 'سُبْحَانَ رَبِّيَ الأَعْلَى',
        transliteration: 'Subhana Rabbiyal A\'la',
        translation: 'Glory be to my Lord, the Most High (3 times)',
        image: 'sujood',
        duration: 8
      },
      {
        instruction: 'Sitting between Sujood',
        arabic: 'رَبِّ اغْفِرْ لِي',
        transliteration: 'Rabbighfir li',
        translation: 'O my Lord, forgive me',
        image: 'sitting',
        duration: 5
      },
      {
        instruction: 'Sujood (Prostration 2)',
        arabic: 'سُبْحَانَ رَبِّيَ الأَعْلَى',
        transliteration: 'Subhana Rabbiyal A\'la',
        translation: 'Glory be to my Lord, the Most High (3 times)',
        image: 'sujood',
        duration: 8
      },
      {
        instruction: 'Tashahhud (Sitting)',
        arabic: 'التَّحِيَّاتُ لِلَّهِ...',
        transliteration: 'At-tahiyyatu lillahi...',
        translation: '(First Tashahhud)',
        image: 'sitting',
        duration: 15
      },

      // --- Raka'at 3 ---
      {
        instruction: 'Stand up for 3rd Raka\'at',
        translation: 'Say "Allahu Akbar" and stand up.',
        duration: 4
      },
      {
        instruction: 'Recite Surah Al-Fatiha',
        arabic: 'بِسْمِ ٱللَّهِ ٱلرَّحْمَـٰنِ ٱلرَّحِيمِ...',
        transliteration: 'Bismillahi r-rahmani r-rahim...',
        translation: '(The Opening Surah) - Recited Silently',
        audioStart: 6.5,
        audioEnd: 65,
        isDynamic: true,
        surahId: 1
      },
      {
        instruction: 'Ruku (Bowing)',
        arabic: 'سُبْحَانَ رَبِّيَ الْعَظِيمِ',
        transliteration: 'Subhana Rabbiyal Azeem',
        translation: 'Glory be to my Lord, the Magnificent (3 times)',
        image: 'ruku',
        duration: 8
      },
      {
        instruction: 'Samiallah (Rising)',
        arabic: 'سَمِعَ اللَّهُ لِمَنْ حَمِدَهُ',
        transliteration: 'Sami Allahu liman hamidah',
        translation: 'Allah hears those who praise Him',
        image: 'standing',
        duration: 4
      },
      {
        instruction: 'Sujood (Prostration 1)',
        arabic: 'سُبْحَانَ رَبِّيَ الأَعْلَى',
        transliteration: 'Subhana Rabbiyal A\'la',
        translation: 'Glory be to my Lord, the Most High (3 times)',
        image: 'sujood',
        duration: 8
      },
      {
        instruction: 'Sitting between Sujood',
        arabic: 'رَبِّ اغْفِرْ لِي',
        transliteration: 'Rabbighfir li',
        translation: 'O my Lord, forgive me',
        image: 'sitting',
        duration: 5
      },
      {
        instruction: 'Sujood (Prostration 2)',
        arabic: 'سُبْحَانَ رَبِّيَ الأَعْلَى',
        transliteration: 'Subhana Rabbiyal A\'la',
        translation: 'Glory be to my Lord, the Most High (3 times)',
        image: 'sujood',
        duration: 8
      },

      // --- Raka'at 4 ---
      {
        instruction: 'Stand up for 4th Raka\'at',
        translation: 'Say "Allahu Akbar" and stand up.',
        duration: 4
      },
      {
        instruction: 'Recite Surah Al-Fatiha',
        arabic: 'بِسْمِ ٱللَّهِ ٱلرَّحْمَـٰنِ ٱلرَّحِيمِ...',
        transliteration: 'Bismillahi r-rahmani r-rahim...',
        translation: '(The Opening Surah) - Recited Silently',
        audioStart: 6.5,
        audioEnd: 65,
        isDynamic: true,
        surahId: 1
      },
      {
        instruction: 'Ruku (Bowing)',
        arabic: 'سُبْحَانَ رَبِّيَ الْعَظِيمِ',
        transliteration: 'Subhana Rabbiyal Azeem',
        translation: 'Glory be to my Lord, the Magnificent (3 times)',
        image: 'ruku',
        duration: 8
      },
      {
        instruction: 'Samiallah (Rising)',
        arabic: 'سَمِعَ اللَّهُ لِمَنْ حَمِدَهُ',
        transliteration: 'Sami Allahu liman hamidah',
        translation: 'Allah hears those who praise Him',
        image: 'standing',
        duration: 4
      },
      {
        instruction: 'Sujood (Prostration 1)',
        arabic: 'سُبْحَانَ رَبِّيَ الأَعْلَى',
        transliteration: 'Subhana Rabbiyal A\'la',
        translation: 'Glory be to my Lord, the Most High (3 times)',
        image: 'sujood',
        duration: 8
      },
      {
        instruction: 'Sitting between Sujood',
        arabic: 'رَبِّ اغْفِرْ لِي',
        transliteration: 'Rabbighfir li',
        translation: 'O my Lord, forgive me',
        image: 'sitting',
        duration: 5
      },
      {
        instruction: 'Sujood (Prostration 2)',
        arabic: 'سُبْحَانَ رَبِّيَ الأَعْلَى',
        transliteration: 'Subhana Rabbiyal A\'la',
        translation: 'Glory be to my Lord, the Most High (3 times)',
        image: 'sujood',
        duration: 8
      },
      {
        instruction: 'Final Tashahhud & Salawat',
        arabic: 'التَّحِيَّاتُ لِلَّهِ...',
        transliteration: 'At-tahiyyatu lillahi...',
        translation: '(Recite full Tashahhud and Durood Ibrahim)',
        image: 'sitting',
        duration: 25
      },
      {
        instruction: 'Tasleem (End)',
        arabic: 'السَّلَامُ عَلَيْكُمْ وَرَحْمَةُ اللهِ',
        transliteration: 'Assalamu alaykum wa rahmatullah',
        translation: 'Peace be upon you and the mercy of Allah (Right then Left)',
        duration: 6
      }
    ],
    audioUrl: 'https://server8.mp3quran.net/afs/001.mp3'
  },
  'Asr': {
    id: 'asr',
    name: 'Asr',
    rakyat: 4,
    type: 'Fard',
    time: 'Mid-Afternoon to Sunset',
    steps: [
      // --- Raka'at 1 ---
      {
        instruction: 'Make Niyyah (Intention) to pray 4 Raka\'at Fard of Asr.',
        arabic: 'نَوَيْتُ صَلَاةَ الْعَصْرِ',
        transliteration: 'Nawaytu salata al-Asr',
        translation: 'I intend to perform the Asr prayer.',
        duration: 5
      },
      {
        instruction: 'Takbir al-Ihram (Start)',
        arabic: 'اللهُ أَكْبَرُ',
        transliteration: 'Allahu Akbar',
        translation: 'Allah is the Greatest',
        image: 'takbir',
        duration: 3
      },
      {
        instruction: 'Recite Surah Al-Fatiha',
        arabic: 'بِسْمِ ٱللَّهِ ٱلرَّحْمَـٰنِ ٱلرَّحِيمِ...',
        transliteration: 'Bismillahi r-rahmani r-rahim...',
        translation: '(The Opening Surah) - Recited Silently',
        audioStart: 6.5,
        audioEnd: 65,
        isDynamic: true,
        surahId: 1
      },
      {
        instruction: 'Recite another Surah',
        arabic: 'قُلْ هُوَ ٱللَّهُ أَحَدٌ...',
        transliteration: 'Qul huwa llāhu aḥad...',
        translation: '(The Sincerity) - Recited Silently',
        customAudioUrl: 'https://server8.mp3quran.net/afs/112.mp3',
        audioStart: 5.5,
        duration: 10,
        isDynamic: true,
        surahId: 112
      },
      {
        instruction: 'Ruku (Bowing)',
        arabic: 'سُبْحَانَ رَبِّيَ الْعَظِيمِ',
        transliteration: 'Subhana Rabbiyal Azeem',
        translation: 'Glory be to my Lord, the Magnificent (3 times)',
        image: 'ruku',
        duration: 8
      },
      {
        instruction: 'Samiallah (Rising)',
        arabic: 'سَمِعَ اللَّهُ لِمَنْ حَمِدَهُ',
        transliteration: 'Sami Allahu liman hamidah',
        translation: 'Allah hears those who praise Him',
        image: 'standing',
        duration: 4
      },
      {
        instruction: 'Rabbana (Standing)',
        arabic: 'رَبَّنَا وَلَكَ الْحَمْدُ',
        transliteration: 'Rabbana wa lakal hamd',
        translation: 'Our Lord, and to You is all praise',
        duration: 4
      },
      {
        instruction: 'Sujood (Prostration 1)',
        arabic: 'سُبْحَانَ رَبِّيَ الأَعْلَى',
        transliteration: 'Subhana Rabbiyal A\'la',
        translation: 'Glory be to my Lord, the Most High (3 times)',
        image: 'sujood',
        duration: 8
      },
      {
        instruction: 'Sitting between Sujood',
        arabic: 'رَبِّ اغْفِرْ لِي',
        transliteration: 'Rabbighfir li',
        translation: 'O my Lord, forgive me',
        image: 'sitting',
        duration: 5
      },
      {
        instruction: 'Sujood (Prostration 2)',
        arabic: 'سُبْحَانَ رَبِّيَ الأَعْلَى',
        transliteration: 'Subhana Rabbiyal A\'la',
        translation: 'Glory be to my Lord, the Most High (3 times)',
        image: 'sujood',
        duration: 8
      },

      // --- Raka'at 2 ---
      {
        instruction: 'Stand up for 2nd Raka\'at',
        translation: 'Say "Allahu Akbar" and stand up.',
        duration: 4
      },
      {
        instruction: 'Recite Surah Al-Fatiha',
        arabic: 'بِسْمِ ٱللَّهِ ٱلرَّحْمَـٰنِ ٱلرَّحِيمِ...',
        transliteration: 'Bismillahi r-rahmani r-rahim...',
        translation: '(The Opening Surah) - Recited Silently',
        audioStart: 6.5,
        audioEnd: 65,
        isDynamic: true,
        surahId: 1
      },
      {
        instruction: 'Recite another Surah',
        arabic: 'قُلْ أَعُوذُ بِرَبِّ ٱلْفَلَقِ...',
        transliteration: 'Qul aʿūdhu bi-rabbi l-falaq...',
        translation: '(The Daybreak) - Recited Silently',
        customAudioUrl: 'https://server8.mp3quran.net/afs/113.mp3',
        audioStart: 6,
        duration: 15,
        isDynamic: true,
        surahId: 113
      },
      {
        instruction: 'Ruku (Bowing)',
        arabic: 'سُبْحَانَ رَبِّيَ الْعَظِيمِ',
        transliteration: 'Subhana Rabbiyal Azeem',
        translation: 'Glory be to my Lord, the Magnificent (3 times)',
        image: 'ruku',
        duration: 8
      },
      {
        instruction: 'Samiallah (Rising)',
        arabic: 'سَمِعَ اللَّهُ لِمَنْ حَمِدَهُ',
        transliteration: 'Sami Allahu liman hamidah',
        translation: 'Allah hears those who praise Him',
        image: 'standing',
        duration: 4
      },
      {
        instruction: 'Sujood (Prostration 1)',
        arabic: 'سُبْحَانَ رَبِّيَ الأَعْلَى',
        transliteration: 'Subhana Rabbiyal A\'la',
        translation: 'Glory be to my Lord, the Most High (3 times)',
        image: 'sujood',
        duration: 8
      },
      {
        instruction: 'Sitting between Sujood',
        arabic: 'رَبِّ اغْفِرْ لِي',
        transliteration: 'Rabbighfir li',
        translation: 'O my Lord, forgive me',
        image: 'sitting',
        duration: 5
      },
      {
        instruction: 'Sujood (Prostration 2)',
        arabic: 'سُبْحَانَ رَبِّيَ الأَعْلَى',
        transliteration: 'Subhana Rabbiyal A\'la',
        translation: 'Glory be to my Lord, the Most High (3 times)',
        image: 'sujood',
        duration: 8
      },
      {
        instruction: 'Tashahhud (Sitting)',
        arabic: 'التَّحِيَّاتُ لِلَّهِ...',
        transliteration: 'At-tahiyyatu lillahi...',
        translation: '(First Tashahhud)',
        image: 'sitting',
        duration: 15
      },

      // --- Raka'at 3 ---
      {
        instruction: 'Stand up for 3rd Raka\'at',
        translation: 'Say "Allahu Akbar" and stand up.',
        duration: 4
      },
      {
        instruction: 'Recite Surah Al-Fatiha',
        arabic: 'بِسْمِ ٱللَّهِ ٱلرَّحْمَـٰنِ ٱلرَّحِيمِ...',
        transliteration: 'Bismillahi r-rahmani r-rahim...',
        translation: '(The Opening Surah) - Recited Silently',
        audioStart: 6.5,
        audioEnd: 65,
        isDynamic: true,
        surahId: 1
      },
      {
        instruction: 'Ruku (Bowing)',
        arabic: 'سُبْحَانَ رَبِّيَ الْعَظِيمِ',
        transliteration: 'Subhana Rabbiyal Azeem',
        translation: 'Glory be to my Lord, the Magnificent (3 times)',
        image: 'ruku',
        duration: 8
      },
      {
        instruction: 'Samiallah (Rising)',
        arabic: 'سَمِعَ اللَّهُ لِمَنْ حَمِدَهُ',
        transliteration: 'Sami Allahu liman hamidah',
        translation: 'Allah hears those who praise Him',
        image: 'standing',
        duration: 4
      },
      {
        instruction: 'Sujood (Prostration 1)',
        arabic: 'سُبْحَانَ رَبِّيَ الأَعْلَى',
        transliteration: 'Subhana Rabbiyal A\'la',
        translation: 'Glory be to my Lord, the Most High (3 times)',
        image: 'sujood',
        duration: 8
      },
      {
        instruction: 'Sitting between Sujood',
        arabic: 'رَبِّ اغْفِرْ لِي',
        transliteration: 'Rabbighfir li',
        translation: 'O my Lord, forgive me',
        image: 'sitting',
        duration: 5
      },
      {
        instruction: 'Sujood (Prostration 2)',
        arabic: 'سُبْحَانَ رَبِّيَ الأَعْلَى',
        transliteration: 'Subhana Rabbiyal A\'la',
        translation: 'Glory be to my Lord, the Most High (3 times)',
        image: 'sujood',
        duration: 8
      },

      // --- Raka'at 4 ---
      {
        instruction: 'Stand up for 4th Raka\'at',
        translation: 'Say "Allahu Akbar" and stand up.',
        duration: 4
      },
      {
        instruction: 'Recite Surah Al-Fatiha',
        arabic: 'بِسْمِ ٱللَّهِ ٱلرَّحْمَـٰنِ ٱلرَّحِيمِ...',
        transliteration: 'Bismillahi r-rahmani r-rahim...',
        translation: '(The Opening Surah) - Recited Silently',
        audioStart: 6.5,
        audioEnd: 65,
        isDynamic: true,
        surahId: 1
      },
      {
        instruction: 'Ruku (Bowing)',
        arabic: 'سُبْحَانَ رَبِّيَ الْعَظِيمِ',
        transliteration: 'Subhana Rabbiyal Azeem',
        translation: 'Glory be to my Lord, the Magnificent (3 times)',
        image: 'ruku',
        duration: 8
      },
      {
        instruction: 'Samiallah (Rising)',
        arabic: 'سَمِعَ اللَّهُ لِمَنْ حَمِدَهُ',
        transliteration: 'Sami Allahu liman hamidah',
        translation: 'Allah hears those who praise Him',
        image: 'standing',
        duration: 4
      },
      {
        instruction: 'Sujood (Prostration 1)',
        arabic: 'سُبْحَانَ رَبِّيَ الأَعْلَى',
        transliteration: 'Subhana Rabbiyal A\'la',
        translation: 'Glory be to my Lord, the Most High (3 times)',
        image: 'sujood',
        duration: 8
      },
      {
        instruction: 'Sitting between Sujood',
        arabic: 'رَبِّ اغْفِرْ لِي',
        transliteration: 'Rabbighfir li',
        translation: 'O my Lord, forgive me',
        image: 'sitting',
        duration: 5
      },
      {
        instruction: 'Sujood (Prostration 2)',
        arabic: 'سُبْحَانَ رَبِّيَ الأَعْلَى',
        transliteration: 'Subhana Rabbiyal A\'la',
        translation: 'Glory be to my Lord, the Most High (3 times)',
        image: 'sujood',
        duration: 8
      },
      {
        instruction: 'Final Tashahhud & Salawat',
        arabic: 'التَّحِيَّاتُ لِلَّهِ...',
        transliteration: 'At-tahiyyatu lillahi...',
        translation: '(Recite full Tashahhud and Durood Ibrahim)',
        image: 'sitting',
        duration: 25
      },
      {
        instruction: 'Tasleem (End)',
        arabic: 'السَّلَامُ عَلَيْكُمْ وَرَحْمَةُ اللهِ',
        transliteration: 'Assalamu alaykum wa rahmatullah',
        translation: 'Peace be upon you and the mercy of Allah (Right then Left)',
        duration: 6
      }
    ],
    audioUrl: 'https://server8.mp3quran.net/afs/001.mp3'
  },
  'Maghrib': {
    id: 'maghrib',
    name: 'Maghrib',
    rakyat: 3,
    type: 'Fard',
    time: 'Sunset to Night',
    steps: [
      // --- Raka'at 1 (Loud) ---
      {
        instruction: 'Make Niyyah (Intention) to pray 3 Raka\'at Fard of Maghrib.',
        arabic: 'نَوَيْتُ صَلَاةَ الْمَغْرِبِ',
        transliteration: 'Nawaytu salata al-Maghrib',
        translation: 'I intend to perform the Maghrib prayer.',
        duration: 5
      },
      {
        instruction: 'Takbir al-Ihram (Start)',
        arabic: 'اللهُ أَكْبَرُ',
        transliteration: 'Allahu Akbar',
        translation: 'Allah is the Greatest',
        image: 'takbir',
        duration: 3
      },
      {
        instruction: 'Recite Surah Al-Fatiha',
        arabic: 'بِسْمِ ٱللَّهِ ٱلرَّحْمَـٰنِ ٱلرَّحِيمِ...',
        transliteration: 'Bismillahi r-rahmani r-rahim...',
        translation: '(The Opening Surah) - Recited Loudly',
        audioStart: 6.5,
        audioEnd: 65,
        isDynamic: true,
        surahId: 1
      },
      {
        instruction: 'Recite another Surah',
        arabic: 'قُلْ هُوَ ٱللَّهُ أَحَدٌ...',
        transliteration: 'Qul huwa llāhu aḥad...',
        translation: '(The Sincerity) - Recited Loudly',
        customAudioUrl: 'https://server8.mp3quran.net/afs/112.mp3',
        audioStart: 5.5,
        duration: 10,
        isDynamic: true,
        surahId: 112
      },
      {
        instruction: 'Ruku (Bowing)',
        arabic: 'سُبْحَانَ رَبِّيَ الْعَظِيمِ',
        transliteration: 'Subhana Rabbiyal Azeem',
        translation: 'Glory be to my Lord, the Magnificent (3 times)',
        image: 'ruku',
        duration: 8
      },
      {
        instruction: 'Samiallah (Rising)',
        arabic: 'سَمِعَ اللَّهُ لِمَنْ حَمِدَهُ',
        transliteration: 'Sami Allahu liman hamidah',
        translation: 'Allah hears those who praise Him',
        image: 'standing',
        duration: 4
      },
      {
        instruction: 'Rabbana (Standing)',
        arabic: 'رَبَّنَا وَلَكَ الْحَمْدُ',
        transliteration: 'Rabbana wa lakal hamd',
        translation: 'Our Lord, and to You is all praise',
        duration: 4
      },
      {
        instruction: 'Sujood (Prostration 1)',
        arabic: 'سُبْحَانَ رَبِّيَ الأَعْلَى',
        transliteration: 'Subhana Rabbiyal A\'la',
        translation: 'Glory be to my Lord, the Most High (3 times)',
        image: 'sujood',
        duration: 8
      },
      {
        instruction: 'Sitting between Sujood',
        arabic: 'رَبِّ اغْفِرْ لِي',
        transliteration: 'Rabbighfir li',
        translation: 'O my Lord, forgive me',
        image: 'sitting',
        duration: 5
      },
      {
        instruction: 'Sujood (Prostration 2)',
        arabic: 'سُبْحَانَ رَبِّيَ الأَعْلَى',
        transliteration: 'Subhana Rabbiyal A\'la',
        translation: 'Glory be to my Lord, the Most High (3 times)',
        image: 'sujood',
        duration: 8
      },

      // --- Raka'at 2 (Loud) ---
      {
        instruction: 'Stand up for 2nd Raka\'at',
        translation: 'Say "Allahu Akbar" and stand up.',
        duration: 4
      },
      {
        instruction: 'Recite Surah Al-Fatiha',
        arabic: 'بِسْمِ ٱللَّهِ ٱلرَّحْمَـٰنِ ٱلرَّحِيمِ...',
        transliteration: 'Bismillahi r-rahmani r-rahim...',
        translation: '(The Opening Surah) - Recited Loudly',
        audioStart: 6.5,
        audioEnd: 65,
        isDynamic: true,
        surahId: 1
      },
      {
        instruction: 'Recite another Surah',
        arabic: 'قُلْ أَعُوذُ بِرَبِّ ٱلْفَلَقِ...',
        transliteration: 'Qul aʿūdhu bi-rabbi l-falaq...',
        translation: '(The Daybreak) - Recited Loudly',
        customAudioUrl: 'https://server8.mp3quran.net/afs/113.mp3',
        audioStart: 6,
        duration: 15,
        isDynamic: true,
        surahId: 113
      },
      {
        instruction: 'Ruku (Bowing)',
        arabic: 'سُبْحَانَ رَبِّيَ الْعَظِيمِ',
        transliteration: 'Subhana Rabbiyal Azeem',
        translation: 'Glory be to my Lord, the Magnificent (3 times)',
        image: 'ruku',
        duration: 8
      },
      {
        instruction: 'Samiallah (Rising)',
        arabic: 'سَمِعَ اللَّهُ لِمَنْ حَمِدَهُ',
        transliteration: 'Sami Allahu liman hamidah',
        translation: 'Allah hears those who praise Him',
        image: 'standing',
        duration: 4
      },
      {
        instruction: 'Sujood (Prostration 1)',
        arabic: 'سُبْحَانَ رَبِّيَ الأَعْلَى',
        transliteration: 'Subhana Rabbiyal A\'la',
        translation: 'Glory be to my Lord, the Most High (3 times)',
        image: 'sujood',
        duration: 8
      },
      {
        instruction: 'Sitting between Sujood',
        arabic: 'رَبِّ اغْفِرْ لِي',
        transliteration: 'Rabbighfir li',
        translation: 'O my Lord, forgive me',
        image: 'sitting',
        duration: 5
      },
      {
        instruction: 'Sujood (Prostration 2)',
        arabic: 'سُبْحَانَ رَبِّيَ الأَعْلَى',
        transliteration: 'Subhana Rabbiyal A\'la',
        translation: 'Glory be to my Lord, the Most High (3 times)',
        image: 'sujood',
        duration: 8
      },
      {
        instruction: 'Tashahhud (Sitting)',
        arabic: 'التَّحِيَّاتُ لِلَّهِ...',
        transliteration: 'At-tahiyyatu lillahi...',
        translation: '(First Tashahhud)',
        image: 'sitting',
        duration: 15
      },

      // --- Raka'at 3 (Silent) ---
      {
        instruction: 'Stand up for 3rd Raka\'at',
        translation: 'Say "Allahu Akbar" and stand up.',
        duration: 4
      },
      {
        instruction: 'Recite Surah Al-Fatiha',
        arabic: 'بِسْمِ ٱللَّهِ ٱلرَّحْمَـٰنِ ٱلرَّحِيمِ...',
        transliteration: 'Bismillahi r-rahmani r-rahim...',
        translation: '(The Opening Surah) - Recited Silently',
        audioStart: 6.5,
        audioEnd: 65,
        isDynamic: true,
        surahId: 1
      },
      {
        instruction: 'Ruku (Bowing)',
        arabic: 'سُبْحَانَ رَبِّيَ الْعَظِيمِ',
        transliteration: 'Subhana Rabbiyal Azeem',
        translation: 'Glory be to my Lord, the Magnificent (3 times)',
        image: 'ruku',
        duration: 8
      },
      {
        instruction: 'Samiallah (Rising)',
        arabic: 'سَمِعَ اللَّهُ لِمَنْ حَمِدَهُ',
        transliteration: 'Sami Allahu liman hamidah',
        translation: 'Allah hears those who praise Him',
        image: 'standing',
        duration: 4
      },
      {
        instruction: 'Sujood (Prostration 1)',
        arabic: 'سُبْحَانَ رَبِّيَ الأَعْلَى',
        transliteration: 'Subhana Rabbiyal A\'la',
        translation: 'Glory be to my Lord, the Most High (3 times)',
        image: 'sujood',
        duration: 8
      },
      {
        instruction: 'Sitting between Sujood',
        arabic: 'رَبِّ اغْفِرْ لِي',
        transliteration: 'Rabbighfir li',
        translation: 'O my Lord, forgive me',
        image: 'sitting',
        duration: 5
      },
      {
        instruction: 'Sujood (Prostration 2)',
        arabic: 'سُبْحَانَ رَبِّيَ الأَعْلَى',
        transliteration: 'Subhana Rabbiyal A\'la',
        translation: 'Glory be to my Lord, the Most High (3 times)',
        image: 'sujood',
        duration: 8
      },
      {
        instruction: 'Final Tashahhud & Salawat',
        arabic: 'التَّحِيَّاتُ لِلَّهِ...',
        transliteration: 'At-tahiyyatu lillahi...',
        translation: '(Recite full Tashahhud and Durood Ibrahim)',
        image: 'sitting',
        duration: 25
      },
      {
        instruction: 'Tasleem (End)',
        arabic: 'السَّلَامُ عَلَيْكُمْ وَرَحْمَةُ اللهِ',
        transliteration: 'Assalamu alaykum wa rahmatullah',
        translation: 'Peace be upon you and the mercy of Allah (Right then Left)',
        duration: 6
      }
    ],
    audioUrl: 'https://server8.mp3quran.net/afs/001.mp3'
  },
  'Isha': {
    id: 'isha',
    name: 'Isha',
    rakyat: 4,
    type: 'Fard',
    time: 'Night to Dawn',
    steps: [
      // --- Raka'at 1 (Loud) ---
      {
        instruction: 'Make Niyyah (Intention) to pray 4 Raka\'at Fard of Isha.',
        arabic: 'نَوَيْتُ صَلَاةَ الْعِشَاءِ',
        transliteration: 'Nawaytu salata al-Isha',
        translation: 'I intend to perform the Isha prayer.',
        duration: 5
      },
      {
        instruction: 'Takbir al-Ihram (Start)',
        arabic: 'اللهُ أَكْبَرُ',
        transliteration: 'Allahu Akbar',
        translation: 'Allah is the Greatest',
        image: 'takbir',
        duration: 3
      },
      {
        instruction: 'Recite Surah Al-Fatiha',
        arabic: 'بِسْمِ ٱللَّهِ ٱلرَّحْمَـٰنِ ٱلرَّحِيمِ...',
        transliteration: 'Bismillahi r-rahmani r-rahim...',
        translation: '(The Opening Surah) - Recited Loudly',
        audioStart: 6.5,
        audioEnd: 65,
        isDynamic: true,
        surahId: 1
      },
      {
        instruction: 'Recite another Surah',
        arabic: 'قُلْ هُوَ ٱللَّهُ أَحَدٌ...',
        transliteration: 'Qul huwa llāhu aḥad...',
        translation: '(The Sincerity) - Recited Loudly',
        customAudioUrl: 'https://server8.mp3quran.net/afs/112.mp3',
        audioStart: 5.5,
        duration: 10,
        isDynamic: true,
        surahId: 112
      },
      {
        instruction: 'Ruku (Bowing)',
        arabic: 'سُبْحَانَ رَبِّيَ الْعَظِيمِ',
        transliteration: 'Subhana Rabbiyal Azeem',
        translation: 'Glory be to my Lord, the Magnificent (3 times)',
        image: 'ruku',
        duration: 8
      },
      {
        instruction: 'Samiallah (Rising)',
        arabic: 'سَمِعَ اللَّهُ لِمَنْ حَمِدَهُ',
        transliteration: 'Sami Allahu liman hamidah',
        translation: 'Allah hears those who praise Him',
        image: 'standing',
        duration: 4
      },
      {
        instruction: 'Rabbana (Standing)',
        arabic: 'رَبَّنَا وَلَكَ الْحَمْدُ',
        transliteration: 'Rabbana wa lakal hamd',
        translation: 'Our Lord, and to You is all praise',
        duration: 4
      },
      {
        instruction: 'Sujood (Prostration 1)',
        arabic: 'سُبْحَانَ رَبِّيَ الأَعْلَى',
        transliteration: 'Subhana Rabbiyal A\'la',
        translation: 'Glory be to my Lord, the Most High (3 times)',
        image: 'sujood',
        duration: 8
      },
      {
        instruction: 'Sitting between Sujood',
        arabic: 'رَبِّ اغْفِرْ لِي',
        transliteration: 'Rabbighfir li',
        translation: 'O my Lord, forgive me',
        image: 'sitting',
        duration: 5
      },
      {
        instruction: 'Sujood (Prostration 2)',
        arabic: 'سُبْحَانَ رَبِّيَ الأَعْلَى',
        transliteration: 'Subhana Rabbiyal A\'la',
        translation: 'Glory be to my Lord, the Most High (3 times)',
        image: 'sujood',
        duration: 8
      },

      // --- Raka'at 2 (Loud) ---
      {
        instruction: 'Stand up for 2nd Raka\'at',
        translation: 'Say "Allahu Akbar" and stand up.',
        duration: 4
      },
      {
        instruction: 'Recite Surah Al-Fatiha',
        arabic: 'بِسْمِ ٱللَّهِ ٱلرَّحْمَـٰنِ ٱلرَّحِيمِ...',
        transliteration: 'Bismillahi r-rahmani r-rahim...',
        translation: '(The Opening Surah) - Recited Loudly',
        audioStart: 6.5,
        audioEnd: 65,
        isDynamic: true,
        surahId: 1
      },
      {
        instruction: 'Recite another Surah',
        arabic: 'قُلْ أَعُوذُ بِرَبِّ ٱلْفَلَقِ...',
        transliteration: 'Qul aʿūdhu bi-rabbi l-falaq...',
        translation: '(The Daybreak) - Recited Loudly',
        customAudioUrl: 'https://server8.mp3quran.net/afs/113.mp3',
        audioStart: 6,
        duration: 15,
        isDynamic: true,
        surahId: 113
      },
      {
        instruction: 'Ruku (Bowing)',
        arabic: 'سُبْحَانَ رَبِّيَ الْعَظِيمِ',
        transliteration: 'Subhana Rabbiyal Azeem',
        translation: 'Glory be to my Lord, the Magnificent (3 times)',
        image: 'ruku',
        duration: 8
      },
      {
        instruction: 'Samiallah (Rising)',
        arabic: 'سَمِعَ اللَّهُ لِمَنْ حَمِدَهُ',
        transliteration: 'Sami Allahu liman hamidah',
        translation: 'Allah hears those who praise Him',
        image: 'standing',
        duration: 4
      },
      {
        instruction: 'Sujood (Prostration 1)',
        arabic: 'سُبْحَانَ رَبِّيَ الأَعْلَى',
        transliteration: 'Subhana Rabbiyal A\'la',
        translation: 'Glory be to my Lord, the Most High (3 times)',
        image: 'sujood',
        duration: 8
      },
      {
        instruction: 'Sitting between Sujood',
        arabic: 'رَبِّ اغْفِرْ لِي',
        transliteration: 'Rabbighfir li',
        translation: 'O my Lord, forgive me',
        image: 'sitting',
        duration: 5
      },
      {
        instruction: 'Sujood (Prostration 2)',
        arabic: 'سُبْحَانَ رَبِّيَ الأَعْلَى',
        transliteration: 'Subhana Rabbiyal A\'la',
        translation: 'Glory be to my Lord, the Most High (3 times)',
        image: 'sujood',
        duration: 8
      },
      {
        instruction: 'Tashahhud (Sitting)',
        arabic: 'التَّحِيَّاتُ لِلَّهِ...',
        transliteration: 'At-tahiyyatu lillahi...',
        translation: '(First Tashahhud)',
        image: 'sitting',
        duration: 15
      },

      // --- Raka'at 3 (Silent) ---
      {
        instruction: 'Stand up for 3rd Raka\'at',
        translation: 'Say "Allahu Akbar" and stand up.',
        duration: 4
      },
      {
        instruction: 'Recite Surah Al-Fatiha',
        arabic: 'بِسْمِ ٱللَّهِ ٱلرَّحْمَـٰنِ ٱلرَّحِيمِ...',
        transliteration: 'Bismillahi r-rahmani r-rahim...',
        translation: '(The Opening Surah) - Recited Silently',
        audioStart: 6.5,
        audioEnd: 65,
        isDynamic: true,
        surahId: 1
      },
      {
        instruction: 'Ruku (Bowing)',
        arabic: 'سُبْحَانَ رَبِّيَ الْعَظِيمِ',
        transliteration: 'Subhana Rabbiyal Azeem',
        translation: 'Glory be to my Lord, the Magnificent (3 times)',
        image: 'ruku',
        duration: 8
      },
      {
        instruction: 'Samiallah (Rising)',
        arabic: 'سَمِعَ اللَّهُ لِمَنْ حَمِدَهُ',
        transliteration: 'Sami Allahu liman hamidah',
        translation: 'Allah hears those who praise Him',
        image: 'standing',
        duration: 4
      },
      {
        instruction: 'Sujood (Prostration 1)',
        arabic: 'سُبْحَانَ رَبِّيَ الأَعْلَى',
        transliteration: 'Subhana Rabbiyal A\'la',
        translation: 'Glory be to my Lord, the Most High (3 times)',
        image: 'sujood',
        duration: 8
      },
      {
        instruction: 'Sitting between Sujood',
        arabic: 'رَبِّ اغْفِرْ لِي',
        transliteration: 'Rabbighfir li',
        translation: 'O my Lord, forgive me',
        image: 'sitting',
        duration: 5
      },
      {
        instruction: 'Sujood (Prostration 2)',
        arabic: 'سُبْحَانَ رَبِّيَ الأَعْلَى',
        transliteration: 'Subhana Rabbiyal A\'la',
        translation: 'Glory be to my Lord, the Most High (3 times)',
        image: 'sujood',
        duration: 8
      },

      // --- Raka'at 4 (Silent) ---
      {
        instruction: 'Stand up for 4th Raka\'at',
        translation: 'Say "Allahu Akbar" and stand up.',
        duration: 4
      },
      {
        instruction: 'Recite Surah Al-Fatiha',
        arabic: 'بِسْمِ ٱللَّهِ ٱلرَّحْمَـٰنِ ٱلرَّحِيمِ...',
        transliteration: 'Bismillahi r-rahmani r-rahim...',
        translation: '(The Opening Surah) - Recited Silently',
        audioStart: 6.5,
        audioEnd: 65,
        isDynamic: true,
        surahId: 1
      },
      {
        instruction: 'Ruku (Bowing)',
        arabic: 'سُبْحَانَ رَبِّيَ الْعَظِيمِ',
        transliteration: 'Subhana Rabbiyal Azeem',
        translation: 'Glory be to my Lord, the Magnificent (3 times)',
        image: 'ruku',
        duration: 8
      },
      {
        instruction: 'Samiallah (Rising)',
        arabic: 'سَمِعَ اللَّهُ لِمَنْ حَمِدَهُ',
        transliteration: 'Sami Allahu liman hamidah',
        translation: 'Allah hears those who praise Him',
        image: 'standing',
        duration: 4
      },
      {
        instruction: 'Sujood (Prostration 1)',
        arabic: 'سُبْحَانَ رَبِّيَ الأَعْلَى',
        transliteration: 'Subhana Rabbiyal A\'la',
        translation: 'Glory be to my Lord, the Most High (3 times)',
        image: 'sujood',
        duration: 8
      },
      {
        instruction: 'Sitting between Sujood',
        arabic: 'رَبِّ اغْفِرْ لِي',
        transliteration: 'Rabbighfir li',
        translation: 'O my Lord, forgive me',
        image: 'sitting',
        duration: 5
      },
      {
        instruction: 'Sujood (Prostration 2)',
        arabic: 'سُبْحَانَ رَبِّيَ الأَعْلَى',
        transliteration: 'Subhana Rabbiyal A\'la',
        translation: 'Glory be to my Lord, the Most High (3 times)',
        image: 'sujood',
        duration: 8
      },
      {
        instruction: 'Final Tashahhud & Salawat',
        arabic: 'التَّحِيَّاتُ لِلَّهِ...',
        transliteration: 'At-tahiyyatu lillahi...',
        translation: '(Recite full Tashahhud and Durood Ibrahim)',
        image: 'sitting',
        duration: 25
      },
      {
        instruction: 'Tasleem (End)',
        arabic: 'السَّلَامُ عَلَيْكُمْ وَرَحْمَةُ اللهِ',
        transliteration: 'Assalamu alaykum wa rahmatullah',
        translation: 'Peace be upon you and the mercy of Allah (Right then Left)',
        duration: 6
      }
    ],
    audioUrl: 'https://server8.mp3quran.net/afs/001.mp3'
  },
  'Sunrise': {
      id: 'sunrise',
      name: 'Sunrise (Shuruq)',
      rakyat: 2,
      type: 'Sunnah',
      time: '15 mins after Sunrise',
      steps: [
          {
            instruction: 'Wait 15-20 minutes after sunrise.',
            translation: 'Do not pray exactly as the sun rises.'
          },
          {
            instruction: 'Perform Salat al-Duha (2 Raka\'at)',
            translation: 'Follow standard 2 Raka\'at prayer steps.'
          }
      ]
  }
};